import flatMap from 'lodash/flatMap';
import uniq from 'lodash/uniq';
import { LifecycleEventType, ReleaseCalendarEventCategory, ReleaseCalendarEventType, ReleaseStatus } from '@/types';
import {
    ReleaseCalendarLifecycleEventData,
    ReleaseCalendarReleaseCycleEventData,
    mapReleaseCalendarColorToAntColor,
} from '@/helpers';
import { WorkflowStepGraph } from '@/types/workflow';
import { dayjs } from '@/extendedDayjs';

interface ArchetypeGroupInfo {
    key: string;
    name: string;
}

export const buildStepConfigIdToArchetypeGroup = (
    stepGraph: WorkflowStepGraph | undefined,
): Record<string, ArchetypeGroupInfo> => {
    const map: Record<string, ArchetypeGroupInfo> = {};
    stepGraph?.stepGroups?.forEach((stepGroup, groupIndex) => {
        stepGroup.archetypeGroups?.forEach((archetypeGroup, archetypeIndex) => {
            const key = `${groupIndex}:${archetypeIndex}:${archetypeGroup.archetype}`;
            archetypeGroup.steps?.forEach((step) => {
                map[step.stepConfigId] = { key, name: archetypeGroup.name };
            });
        });
    });
    return map;
};

const dateCategoryForEvent = (event: ReleaseCalendarLifecycleEventData): ReleaseCalendarEventCategory =>
    event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate ?
        ReleaseCalendarEventCategory.ReleaseCalendarEventCategoryActual
    :   ReleaseCalendarEventCategory.ReleaseCalendarEventCategoryExpected;

const destinationIdsForLifecycleEvent = (event: ReleaseCalendarLifecycleEventData): string[] => {
    if (event.destinationIds?.length) {
        return event.destinationIds;
    }
    if (event.destinationId) {
        return [event.destinationId];
    }
    return event.stepConfigId ? [event.stepConfigId] : [];
};

const sourceEventsForLifecycleEvent = (event: ReleaseCalendarLifecycleEventData): ReleaseCalendarLifecycleEventData[] =>
    event.groupedEvents ?? [event];

const recolorByFlightpath = <T extends { appId?: string; status: ReleaseStatus }>(
    event: T,
    flightpathColorByAppId: Record<string, string>,
): string | undefined => {
    const flightpathColor = event.appId ? flightpathColorByAppId[event.appId] : undefined;
    if (!flightpathColor) {
        return undefined;
    }
    const colorResult = mapReleaseCalendarColorToAntColor(flightpathColor, event.status);
    return colorResult ? colorResult[0] : undefined;
};

export const mergeLifecycleEventsForFlightpaths = (
    events: ReleaseCalendarLifecycleEventData[],
    stepConfigIdToGroup: Record<string, ArchetypeGroupInfo>,
    flightpathColorByAppId: Record<string, string> = {},
): ReleaseCalendarLifecycleEventData[] => {
    const mergedByKey = new Map<string, ReleaseCalendarLifecycleEventData>();

    events.forEach((event) => {
        if (event.eventType === LifecycleEventType.LifecycleEventTypeReleaseCycle) {
            mergedByKey.set(`releaseCycle|${event.id}`, event);
            return;
        }

        const group = event.stepConfigId ? stepConfigIdToGroup[event.stepConfigId] : undefined;
        const groupKey = group?.key ?? event.eventType;
        const runKey = event.workflowRunId ?? event.groupId;
        const isIncrement = event.eventType === LifecycleEventType.LifecycleEventTypeRolloutIncrement;
        const incrementKey = isIncrement ? (event.incrementDayNumber ?? '') : '';
        const mergeKey = `${runKey}|${groupKey}|${event.eventType}|${dateCategoryForEvent(event)}|${incrementKey}`;

        const existing = mergedByKey.get(mergeKey);
        if (!existing) {
            mergedByKey.set(mergeKey, {
                ...event,
                destinationIds: destinationIdsForLifecycleEvent(event),
                groupedEvents: sourceEventsForLifecycleEvent(event),
            });
            return;
        }

        const destinationIds = uniq([...(existing.destinationIds ?? []), ...destinationIdsForLifecycleEvent(event)]);
        const representative = dayjs(event.start).isAfter(dayjs(existing.start)) ? event : existing;
        mergedByKey.set(mergeKey, {
            ...representative,
            id: existing.id,
            destinationIds,
            groupedEvents: [...sourceEventsForLifecycleEvent(existing), ...sourceEventsForLifecycleEvent(event)],
        });
    });

    return Array.from(mergedByKey.values()).map((event) => {
        const backgroundColor = recolorByFlightpath(event, flightpathColorByAppId);
        return backgroundColor ? { ...event, backgroundColor } : event;
    });
};

export const buildFlightpathsViewReleaseCycleEvents = (
    releaseCycleEvents: ReleaseCalendarReleaseCycleEventData[],
    flightpathColorByAppId: Record<string, string> = {},
): ReleaseCalendarReleaseCycleEventData[] =>
    releaseCycleEvents.map((event) => {
        const flightpathColor = event.appId ? flightpathColorByAppId[event.appId] : undefined;
        const colorResult = flightpathColor ? mapReleaseCalendarColorToAntColor(flightpathColor, event.status) : null;
        return colorResult ? { ...event, backgroundColor: colorResult[1], parentEventColor: colorResult[0] } : event;
    });

export const RELEASE_CALENDAR_DESTINATION_COLORS: string[] = [
    'blue',
    'orange',
    'green',
    'purple',
    'pink',
    'teal',
    'brown',
];

export const computeCyclingColorsByKey = (keys: string[]): Record<string, string> => {
    const colorByKey: Record<string, string> = {};
    [...keys].sort().forEach((key, index) => {
        colorByKey[key] = RELEASE_CALENDAR_DESTINATION_COLORS[index % RELEASE_CALENDAR_DESTINATION_COLORS.length];
    });
    return colorByKey;
};

export const computeDestinationColorsByDestinationId = (destinationIds: string[]): Record<string, string> =>
    computeCyclingColorsByKey(destinationIds);

export interface StripedDayBand {
    startFraction: number;
    endFraction: number;
}

export interface StripedDateRange {
    start: string;
    end?: string;
}

const MILLIS_PER_DAY = 24 * 60 * 60 * 1000;

const BAND_FRACTION_TOLERANCE = 0.02;

const mergeDayBands = (bands: StripedDayBand[]): StripedDayBand[] => {
    const sorted = [...bands].sort((a, b) => a.startFraction - b.startFraction);
    const merged: StripedDayBand[] = [];
    sorted.forEach((band) => {
        const last = merged[merged.length - 1];
        if (last && band.startFraction <= last.endFraction + BAND_FRACTION_TOLERANCE) {
            last.endFraction = Math.max(last.endFraction, band.endFraction);
        } else {
            merged.push({ ...band });
        }
    });
    return merged;
};

export const computeStripedDayBands = (ranges: StripedDateRange[]): Record<string, StripedDayBand[]> => {
    const bandsByDate: Record<string, StripedDayBand[]> = {};
    ranges.forEach(({ start, end }) => {
        const rangeStart = dayjs(start);
        const rangeEnd = end ? dayjs(end) : rangeStart.startOf('day').add(1, 'day');
        if (!rangeEnd.isAfter(rangeStart)) {
            return;
        }
        let cursor = rangeStart.startOf('day');
        while (cursor.isBefore(rangeEnd)) {
            const dayStart = cursor;
            const dayEnd = cursor.add(1, 'day');
            const coverStart = rangeStart.isAfter(dayStart) ? rangeStart : dayStart;
            const coverEnd = rangeEnd.isBefore(dayEnd) ? rangeEnd : dayEnd;
            const startFraction = coverStart.diff(dayStart) / MILLIS_PER_DAY;
            const endFraction = coverEnd.diff(dayStart) / MILLIS_PER_DAY;
            const dateKey = dayStart.format('YYYY-MM-DD');
            (bandsByDate[dateKey] ??= []).push({ startFraction, endFraction });
            cursor = dayEnd;
        }
    });
    Object.keys(bandsByDate).forEach((dateKey) => {
        bandsByDate[dateKey] = mergeDayBands(bandsByDate[dateKey]);
    });
    return bandsByDate;
};

export const subtractDayBands = (bands: StripedDayBand[], coveringBands: StripedDayBand[] = []): StripedDayBand[] => {
    let remaining = bands.map((band) => ({ ...band }));
    coveringBands.forEach((covering) => {
        remaining = flatMap(remaining, (band) => {
            if (covering.endFraction <= band.startFraction || covering.startFraction >= band.endFraction) {
                return [band];
            }
            const pieces: StripedDayBand[] = [];
            if (covering.startFraction > band.startFraction) {
                pieces.push({ startFraction: band.startFraction, endFraction: covering.startFraction });
            }
            if (covering.endFraction < band.endFraction) {
                pieces.push({ startFraction: covering.endFraction, endFraction: band.endFraction });
            }
            return pieces;
        });
    });
    return remaining.filter((band) => band.endFraction - band.startFraction > BAND_FRACTION_TOLERANCE);
};

export const isFullDayBand = (bands: StripedDayBand[]): boolean =>
    bands.length === 1 &&
    bands[0].startFraction <= BAND_FRACTION_TOLERANCE &&
    bands[0].endFraction >= 1 - BAND_FRACTION_TOLERANCE;

export const bandsToMaskGradient = (bands: StripedDayBand[]): string => {
    const stops: string[] = [];
    bands.forEach(({ startFraction, endFraction }) => {
        const startPct = Math.max(0, startFraction) * 100;
        const endPct = Math.min(1, endFraction) * 100;
        stops.push(`transparent ${startPct}%`, `black ${startPct}%`, `black ${endPct}%`, `transparent ${endPct}%`);
    });
    return `linear-gradient(to right, ${stops.join(', ')})`;
};

const destinationViewGroupId = (releaseId: string, destinationId: string): string => `${releaseId}::${destinationId}`;

const isDestinationVisible = (destinationId: string | undefined, selectedDestinationIds: string[]): boolean =>
    selectedDestinationIds.length === 0 || !destinationId || selectedDestinationIds.includes(destinationId);

export const buildDestinationsViewLifecycleEvents = (
    events: ReleaseCalendarLifecycleEventData[],
    selectedDestinationIds: string[] = [],
): ReleaseCalendarLifecycleEventData[] =>
    events
        .filter((event) => isDestinationVisible(event.destinationId, selectedDestinationIds))
        .map((event) => {
            if (!event.destinationId || !event.destinationColor) {
                return event;
            }
            const colorResult = mapReleaseCalendarColorToAntColor(event.destinationColor, event.status);
            const backgroundColor = colorResult ? colorResult[0] : event.backgroundColor;
            return {
                ...event,
                backgroundColor,
                groupId: destinationViewGroupId(event.groupId, event.destinationId),
            };
        });

export const buildDestinationsViewReleaseCycleEvents = (
    lifecycleEvents: ReleaseCalendarLifecycleEventData[],
    releaseCycleEvents: ReleaseCalendarReleaseCycleEventData[],
    selectedDestinationIds: string[] = [],
): ReleaseCalendarReleaseCycleEventData[] => {
    const releaseCycleByGroupId = new Map(releaseCycleEvents.map((event) => [event.groupId, event]));

    interface DestinationBarAccumulator {
        releaseId: string;
        destinationId: string;
        destinationColor?: string;
        status: ReleaseStatus;
        earliestStart: string;
        latestEnd: string;
    }

    const barsByGroupId = new Map<string, DestinationBarAccumulator>();

    lifecycleEvents.forEach((event) => {
        if (!event.destinationId || !isDestinationVisible(event.destinationId, selectedDestinationIds)) {
            return;
        }
        const groupId = destinationViewGroupId(event.groupId, event.destinationId);
        const eventEnd = event.end ?? event.start;
        const existing = barsByGroupId.get(groupId);
        if (!existing) {
            barsByGroupId.set(groupId, {
                releaseId: event.groupId,
                destinationId: event.destinationId,
                destinationColor: event.destinationColor,
                status: event.status,
                earliestStart: event.start,
                latestEnd: eventEnd,
            });
            return;
        }
        if (dayjs(event.start).isBefore(dayjs(existing.earliestStart))) {
            existing.earliestStart = event.start;
        }
        if (dayjs(eventEnd).isAfter(dayjs(existing.latestEnd))) {
            existing.latestEnd = eventEnd;
        }
    });

    const destinationBars: ReleaseCalendarReleaseCycleEventData[] = [];
    barsByGroupId.forEach((accumulator, groupId) => {
        const baseEvent = releaseCycleByGroupId.get(accumulator.releaseId);
        if (!baseEvent) {
            return;
        }
        const colorResult =
            accumulator.destinationColor ?
                mapReleaseCalendarColorToAntColor(accumulator.destinationColor, accumulator.status)
            :   null;
        const backgroundColor = colorResult ? colorResult[1] : baseEvent.backgroundColor;
        const parentEventColor = colorResult ? colorResult[0] : baseEvent.parentEventColor;

        destinationBars.push({
            ...baseEvent,
            id: `${baseEvent.id}::${accumulator.destinationId}`,
            groupId,
            start: accumulator.earliestStart,
            end: dayjs(accumulator.latestEnd).add(1, 'day').format(),
            backgroundColor,
            parentEventColor,
        });
    });

    return destinationBars;
};
